/**
 * ansu-reviews.js — Ansugifts reviews
 * Native scroll-snap slider + voortgangsbalk + "lees meer" + foto-lightbox.
 */
(function () {
  'use strict';
  if (customElements.get('ansu-reviews-slider')) return;

  class AnsuReviewsSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ars-track');
      if (!this.track) return;
      this.cards = Array.from(this.track.querySelectorAll('.ars-card'));
      this.prev = this.querySelector('.ars-arrow--prev');
      this.next = this.querySelector('.ars-arrow--next');
      this.bar = this.querySelector('.ars-progress__bar');
      this.dialog = this.querySelector('.ars-lightbox');
      this.autoplay = this.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;

      this.prev && this.prev.addEventListener('click', () => this.step(-1));
      this.next && this.next.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      new ResizeObserver(() => { this.update(); this.checkClamps(); }).observe(this.track);

      this.bindDrag();
      this.bindMore();
      this.bindLightbox();
      this.bindKeys();

      if (this.autoplay) {
        this.start();
        ['pointerenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => this.stop(), { passive: true }));
        this.addEventListener('pointerleave', () => this.start());
      }
      requestAnimationFrame(() => { this.update(); this.checkClamps(); });
    }

    disconnectedCallback() { this.stop(); }

    cardStep() {
      const c = this.cards[0];
      if (!c) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return c.getBoundingClientRect().width + gap;
    }

    step(dir) {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (dir > 0 && this.track.scrollLeft >= max) { this.track.scrollTo({ left: 0 }); return; }
      this.track.scrollBy({ left: dir * this.cardStep() });
    }

    update() {
      const t = this.track;
      const max = Math.max(1, t.scrollWidth - t.clientWidth);
      const ratio = Math.min(1, t.clientWidth / t.scrollWidth);
      const pos = t.scrollLeft / max;
      if (this.bar) {
        this.bar.style.width = (ratio * 100) + '%';
        this.bar.style.transform = `translateX(${pos * (1 / ratio - 1) * 100}%)`;
      }
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = !this.autoplay && t.scrollLeft >= max - 2;
      const hide = t.scrollWidth <= t.clientWidth + 2;
      const controls = this.querySelector('.ars-controls');
      if (controls) controls.hidden = hide;
    }

    start() { this.stop(); this.timer = setInterval(() => this.step(1), this.speed); }
    stop() { clearInterval(this.timer); this.timer = null; }

    bindDrag() {
      let down = false, startX = 0, startLeft = 0, moved = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) { moved = true; this.track.classList.add('is-dragging'); }
        if (moved) this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (moved) {
          this.track.classList.remove('is-dragging');
          const s = this.cardStep();
          this.track.scrollTo({ left: Math.round(this.track.scrollLeft / s) * s });
          this.track.addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); }, { capture: true, once: true });
        }
      });
    }

    bindMore() {
      this.querySelectorAll('.ars-more').forEach((btn) => {
        btn.addEventListener('click', () => {
          const text = btn.previousElementSibling;
          const open = text.classList.toggle('is-open');
          btn.textContent = open ? btn.dataset.less : btn.dataset.more;
          btn.setAttribute('aria-expanded', open);
        });
      });
    }
    checkClamps() {
      this.querySelectorAll('.ars-card__text').forEach((el) => {
        const btn = el.nextElementSibling;
        if (!btn || !btn.classList.contains('ars-more') || el.classList.contains('is-open')) return;
        btn.hidden = el.scrollHeight <= el.clientHeight + 2;
      });
    }

    bindLightbox() {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      const media = Array.from(this.querySelectorAll('.ars-card__media[data-full]'));
      const img = this.dialog.querySelector('.ars-lightbox__img');
      const txt = this.dialog.querySelector('.ars-lightbox__text');
      const name = this.dialog.querySelector('.ars-lightbox__name');
      const label = this.dialog.querySelector('.ars-lightbox__label');
      let i = 0;
      const show = (n) => {
        i = (n + media.length) % media.length;
        const m = media[i];
        const card = m.closest('.ars-card');
        img.src = m.dataset.full;
        img.alt = m.querySelector('img') ? m.querySelector('img').alt : '';
        const t = card.querySelector('.ars-card__text');
        txt.textContent = t ? t.textContent.trim() : '';
        const nm = card.querySelector('.ars-card__name');
        name.textContent = nm ? nm.textContent.trim() : '';
        const lb = card.querySelector('.ars-card__label');
        label.textContent = lb ? lb.textContent.trim().replace(/\s+/g, ' ') : '';
      };
      media.forEach((m, n) => m.addEventListener('click', () => { this.stop(); show(n); this.dialog.showModal(); }));
      this.dialog.querySelector('.ars-lightbox__close').addEventListener('click', () => this.dialog.close());
      this.dialog.querySelector('.ars-lightbox__nav--prev').addEventListener('click', () => show(i - 1));
      this.dialog.querySelector('.ars-lightbox__nav--next').addEventListener('click', () => show(i + 1));
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') show(i - 1);
        if (e.key === 'ArrowRight') show(i + 1);
      });
      if (media.length < 2) this.dialog.querySelectorAll('.ars-lightbox__nav').forEach((b) => (b.hidden = true));
    }

    bindKeys() {
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { this.step(-1); e.preventDefault(); }
        if (e.key === 'ArrowRight') { this.step(1); e.preventDefault(); }
      });
    }
  }

  customElements.define('ansu-reviews-slider', AnsuReviewsSlider);
})();
